import {
  createContext,
  useContext,
  useMemo,
  type PropsWithChildren,
  type FunctionComponent,
} from 'react'
import { useQualityScorecardsData } from '../api/qualityScorecards.hooks'

export interface QualityScorecard {
  ippTotemEntity?: string
  ippToolName?: string
  team?: string
  monthlyViews?: number
  p90LoadingTime?: number
  viewsWithErrorsPercentage?: number
}

export interface QualityScorecardsContextValue {
  scorecardsMap: Map<string, QualityScorecard>
  loading: boolean
  error?: Error
}

const QualityScorecardsContext = createContext<QualityScorecardsContextValue | undefined>(undefined)

export const QualityScorecardsProvider: FunctionComponent<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { scorecards, loading, error } = useQualityScorecardsData({
    limit: 500, // Fetch all scorecards
  })

  const scorecardsMap = useMemo(() => {
    const map = new Map<string, QualityScorecard>()
    scorecards.forEach(scorecard => {
      if (scorecard.ippTotemEntity) {
        map.set(scorecard.ippTotemEntity, scorecard)
      }
    })
    return map
  }, [scorecards])

  const value = useMemo(
    () => ({
      scorecardsMap,
      loading,
      error: error ? (error as Error) : undefined,
    }),
    [scorecardsMap, loading, error]
  )

  return (
    <QualityScorecardsContext.Provider value={value}>{children}</QualityScorecardsContext.Provider>
  )
}

export const useQualityScorecardsContext = () => {
  const context = useContext(QualityScorecardsContext)
  if (!context) {
    throw new Error('useQualityScorecardsContext must be used within QualityScorecardsProvider')
  }
  return context
}

/**
 * Hook to get quality scorecard data for a specific tool
 * @param ippTotemEntity - The totem entity identifier (e.g., from totemInfo?.routePattern)
 * @returns The quality scorecard data for the specified tool, or undefined if not found
 */
export const useQualityScorecard = (ippTotemEntity?: string): QualityScorecard | undefined => {
  const { scorecardsMap } = useQualityScorecardsContext()

  if (!ippTotemEntity) {
    return undefined
  }

  return scorecardsMap.get(ippTotemEntity)
}
